 
import URLRenderer from '@/app/common/components/CellRenderers/URLRenderer';
import StatusCodeRenderer from '@/app/common/components/CellRenderers/StatusCodeRenderer';
import BadgeRenderer from '@/app/common/components/CellRenderers/BadgeRenderer';
import { getColor } from '@/helpers/utils';
import ArrayRenderer from '@/app/common/components/CellRenderers/ArrayRenderer';
import BooleanRenderer from '@/app/common/components/CellRenderers/BooleanRenderer';
import ProtocolRenderer from '@/app/common/components/CellRenderers/ProtocolRenderer';
import HtmlParser from '@/app/common/components/CellRenderers/HtmlParser';
import { AiOutlineZoomIn } from 'react-icons/ai';
import { useSearchParams } from 'react-router-dom';
import typeToDetail from '@/helpers/typeToDetail';
import typeToFa from '@/helpers/typeToFa';
import { FiCornerRightDown } from 'react-icons/fi';
import { link_hrefColumn } from './columns';
import { crawlTooltip } from '@/app/Crawl/tooltip/crawlTooltips';
import CrawlStatusChecker from '@/app/Crawl/components/common/CrawlStatusChecker';
import CrawlStatusError from '@/app/Crawl/components/common/CrawlStatusError';

import type { ICellRendererParams } from 'ag-grid-community';
import type { ReactNode } from 'react';

type CrawlRow = Record<string, any>;
type CrawlCellParams = Omit<ICellRendererParams<CrawlRow, any>, 'data'> & {
  data: CrawlRow;
};
type CrawlColumnProps = Record<string, unknown>;
type CrawlColumn = {
  [key: string]: unknown;
  cellRenderer?: (params: CrawlCellParams) => ReactNode;
};
type ChecklistCategory = {
  [key: string]: unknown;
  columns: CrawlColumn[];
};
type CheckListConfig = {
  errors: ChecklistCategory[];
  warning: ChecklistCategory[];
  info: ChecklistCategory[];
};

// SPECIFIC COLUMN

const urlColumnWithoutFlex = (
  props: CrawlColumnProps = {}
): CrawlColumn => ({
  field: 'url',
  type: 'string',
  // tooltip: crawlTooltip.columns.url,
  flex: 0,
  title: 'نشانی',
  width: 350,
  searchable: true,
  gp: 'نشانی (آدرس صفحه)',
  cellRenderer: params => {
    const { data } = params;
    if (!data) return null;

    return (
      <URLRenderer
        url={data.url}
        job_page={data.job_page}
        status_code={data.status_code}
        type={data.type || ''}
        queries={['issues']}
        details={typeToDetail(data.type) || 'internal'}
        activeTab={
          data.type
            ? typeToDetail(data.type) === 'internal'
              ? 'overview'
              : typeToDetail(data.type)
            : 'overview'
        }
        {...props}
      />
    );
  }
});

const similar_title_urlColumn = (
  props: CrawlColumnProps = {}
): CrawlColumn => ({
  field: 'url',
  type: 'string',
  flex: 1,
  title: 'نشانی',
  minWidth: 300,
  searchable: true,
  cellRenderer: params => {
    return (
      <URLRenderer
        url={params.data.url}
        job_page={params.data.job_page}
        status_code={params.data.status_code}
        type={params.data.type || ''}
        queries={['issues']}
        details={typeToDetail(params.data.type) || 'internal'}
        activeTab={
          params.data.type
            ? typeToDetail(params.data.type) === 'internal'
              ? 'overview'
              : typeToDetail(params.data.type)
            : 'overview'
        }
        {...props}
      />
    );
  }
});

const ps_urlColumn = (
  props: CrawlColumnProps = {}
): CrawlColumn => ({
  // tooltip: crawlTooltip.columns.url,
  field: 'ps_url',
  flex: 1,
  minWidth: 300,
  type: 'string',
  title: 'صفحه مرجع',
  searchable: true,
  cellRenderer: params => {
    const { data } = params;
    if (!data) return null;

    return (
      <URLRenderer
        url={data.ps_url}
        job_page={data.job_page}
        status_code={data.status_code}
        type={data.type || ''}
        queries={['issues']}
        details={typeToDetail(data.type) || 'internal'}
        activeTab={
          data.type
            ? typeToDetail(data.type) === 'internal'
              ? 'overview'
              : typeToDetail(data.type)
            : 'overview'
        }
        {...props}
      />
    );
  }
});

const unknown_urlColumn: CrawlColumn = {
  field: 'link_href',
  flex: 1,
  minWidth: 300,
  type: 'string',
  title: 'پیوند ناشناخته',
  searchable: true,
  cellRenderer: params => {
    const { data } = params;
    if (!data) return null;

    return (
      <div style={{ direction: 'ltr', textAlign: 'left' }}>
        {data.link_href}
      </div>
    );
  }
};

const urlColumn = (
  props: CrawlColumnProps = {}
): CrawlColumn => ({
  // tooltip: crawlTooltip.columns.url,
  field: 'url',
  type: 'string',
  flex: 1,
  title: 'نشانی',
  minWidth: 180,
  searchable: true,
  gp: 'نشانی (آدرس صفحه)',
  cellRenderer: params => {
    const { data } = params;
    if (!data) return null;

    return (
      <URLRenderer
        url={data.url}
        job_page={data.job_page}
        status_code={data.status_code}
        type={data.type || ''}
        queries={['issues']}
        details={typeToDetail(data.type) || 'internal'}
        activeTab={
          data.type
            ? typeToDetail(data.type) === 'internal'
              ? 'overview'
              : typeToDetail(data.type)
            : 'overview'
        }
        {...props}
      />
    );
  }
});

const typeColumn: CrawlColumn = {
  field: 'type',
  type: 'string',
  title: 'نوع',
  searchable: true,
  tooltip: crawlTooltip.columns.type,
  cellRenderer: params => <>{typeToFa(params.value)}</>
};

const page_link_typeColumn: CrawlColumn = {
  field: 'page_link_type',
  type: 'string',
  title: 'نوع پیوند',
  searchable: true,
  cellRenderer: params => {
    const { data } = params;
    if (!data) return null;

    return data.page_link_type === 'internal' ? (
      <span>داخلی</span>
    ) : (
      <span>خارجی</span>
    );
  }
};

const sizeKBColumn: CrawlColumn = {
  field: 'size_KB',
  type: 'number',
  width: 150,
  title: 'اندازه (KB)',
  searchable: true
  // ,  tooltip: crawlTooltip.columns.sizeKBColumn,
};

const lastMdifiedByDay: CrawlColumn = {
  field: 'last_modified_by_day',
  type: 'int',
  title: 'چند روز آپدیت نشده؟',
  searchable: true,
  tooltip: crawlTooltip.columns.last_modified_by_day
};

const lastModified: CrawlColumn = {
  field: 'last_modified',
  type: 'string',
  title: 'آخرین به‌روزرسانی',
  searchable: true,
  tooltip: crawlTooltip.columns.last_modified
};

const publishedTime: CrawlColumn = {
  field: 'published_time',
  type: 'string',
  title: 'تاریخ انتشار',
  searchable: true,
  tooltip: crawlTooltip.columns.published_time
};

const link_textColumn: CrawlColumn = {
  tooltip: crawlTooltip.columns.link_text,
  field: 'link_text',
  type: 'string',
  title: 'متن پیوند',
  searchable: true
};

const link_is_nofollowColumn: CrawlColumn = {
  field: 'link_is_nofollow',
  type: 'boolean',
  title: 'link_is_nofollow',
  searchable: true,
  cellRenderer: params => (
    <BooleanRenderer boolValue={params.data.link_is_nofollow} />
  )
};

const link_is_noopenerColumn: CrawlColumn = {
  field: 'link_is_noopener',
  type: 'boolean',
  title: 'link_is_noopener',
  searchable: true,
  cellRenderer: params => (
    <BooleanRenderer boolValue={params.data.link_is_noopener} />
  )
};
const link_is_noreferrerColumn: CrawlColumn = {
  field: 'link_is_noreferrer',
  type: 'boolean',
  title: 'link_is_noreferrer',
  searchable: true,
  cellRenderer: params => (
    <BooleanRenderer boolValue={params.data.link_is_noreferrer} />
  )
};
const link_is_alternateColumn: CrawlColumn = {
  field: 'link_is_alternate',
  type: 'boolean',
  title: 'link_is_alternate',
  cellRenderer: params => (
    <BooleanRenderer boolValue={params.data.link_is_alternate} />
  )
};

const link_is_ugcColumn: CrawlColumn = {
  field: 'link_is_ugc',
  type: 'boolean',
  title: 'link_is_ugc',
  cellRenderer: params => (
    <BooleanRenderer boolValue={params.data.link_is_ugc} />
  )
};
const link_is_sponsoredColumn: CrawlColumn = {
  field: 'link_is_sponsored',
  type: 'boolean',
  title: 'link_is_sponsored',
  cellRenderer: params => (
    <BooleanRenderer boolValue={params.data.link_is_sponsored} />
  )
};
const link_is_tagColumn: CrawlColumn = {
  field: 'link_is_tag',
  type: 'boolean',
  title: 'link_is_tag',
  cellRenderer: params => (
    <BooleanRenderer boolValue={params.data.link_is_tag} />
  )
};
const link_is_publisherColumn: CrawlColumn = {
  field: 'link_is_publisher',
  type: 'boolean',
  title: 'link_is_publisher',
  cellRenderer: params => (
    <BooleanRenderer boolValue={params.data.link_is_publisher} />
  )
};
const link_is_licenseColumn: CrawlColumn = {
  field: 'link_is_license',
  type: 'boolean',
  title: 'link_is_license'
};
const link_is_authorColumn: CrawlColumn = {
  field: 'link_is_author',
  type: 'boolean',
  title: 'link_is_author'
};
const link_is_bookmarkColumn: CrawlColumn = {
  field: 'link_is_bookmark',
  type: 'boolean',
  title: 'link_is_bookmark',
  cellRenderer: params => (
    <BooleanRenderer boolValue={params.data.link_is_bookmark} />
  )
};
const link_href_protocolColumn: CrawlColumn = {
  field: 'link_href_protocol',
  type: 'string',
  title: 'link_href_protocol',
  cellRenderer: params => (
    <ProtocolRenderer protocol={params.data.link_href_protocol} />
  )
};
const link_href_fragmentColumn: CrawlColumn = {
  field: 'link_href_fragment',
  type: 'string',
  title: 'link_href_fragment'
};
const link_href_queryColumn: CrawlColumn = {
  field: 'link_href_query',
  type: 'string',
  title: 'link_href_query'
};
const link_href_query_countColumn: CrawlColumn = {
  field: 'link_href_query_count',
  type: 'number',
  title: 'link_href_query_count'
};
const link_href_typeColumn: CrawlColumn = {
  field: 'link_href_type',
  type: 'string',
  title: 'link_href_type'
};
const link_is_imgColumn: CrawlColumn = {
  field: 'link_is_img',
  type: 'boolean',
  title: 'link_is_img',
  cellRenderer: params => (
    <BooleanRenderer boolValue={params.data.link_is_img} />
  )
};
const link_alt_imgColumn: CrawlColumn = {
  field: 'link_alt_img',
  type: 'string',
  title: 'link_alt_img'
};
const link_src_imgColumn: CrawlColumn = {
  field: 'link_src_img',
  type: 'string',
  title: 'link_src_img'
};
const link_3xx_destinationColumn: CrawlColumn = {
  field: 'link_3xx_destination',
  type: 'string',
  title: 'link_3xx_destination'
};
const link_positionColumn: CrawlColumn = {
  field: 'link_position',
  type: 'string',
  title: 'link_position'
};
// const link_hrefColumn = {
//   field: 'link_href',
//   type: 'string',
//   title: 'link_href',
//   width: 300,
//   searchable: true,
//   cellRenderer: params => (
//     <URLRenderer
//       url={params.data.link_href}
//       details="internal"
//       job_page={params.data.job_page}
//       status_code={params.data.status_code}
//       activeTab="overview"
//     />
//   )
// };

const titleColumn: CrawlColumn = {
  field: 'title',
  type: 'string',
  width: 350,
  title: 'عنوان صفحه',
  searchable: true
};

const similar_contentColumn: CrawlColumn = {
  flex: 1,
  field: 'title',
  type: 'string',
  width: 350,
  title: 'عنوان',
  searchable: true
};

const similarTitleColumn: CrawlColumn = {
  field: 'title',
  flex: 1,
  type: 'string',
  title: 'گروه',
  searchable: true,
  cellRenderer: 'agGroupCellRenderer'
};

const titleNotUsingInTextColumn: CrawlColumn = {
  field: 'title_not_using_in_text',
  type: 'string',
  width: 270,
  title: 'کلمات استفاده نشده تگ عنوان در متن',
  searchable: true,
  cellRenderer: params => (
    <HtmlParser html={params.data.title_not_using_in_text} />
  )
};

const h1NotUsingInTextColumn: CrawlColumn = {
  field: 'h1_not_using_in_text',
  type: 'string',
  width: 270,
  gp: 'H1',
  title: 'کلمات استفاده نشده تگ H1 در متن',
  searchable: true,
  cellRenderer: params => {
    const { data } = params;
    if (!data) return null;

    return (
      <div style={{ textAlign: 'left', direction: 'ltr', width: '100%' }}>
        {data.h1_not_using_in_text.length !== 0
          ? data.h1_not_using_in_text.map((item, index) => (
              <>
                <HtmlParser html={item} />
              </>
            ))
          : '-'}
      </div>
    );
  }
};

const h2NotUsingInTextColumn: CrawlColumn = {
  field: 'h2_not_using_in_text',
  type: 'string',
  width: 270,
  gp: 'H2',
  title: 'کلمات استفاده نشده تگ H2 در متن',
  searchable: true,
  cellRenderer: params => {
    const { data } = params;
    if (!data) return null;

    return (
      <div style={{ textAlign: 'left', direction: 'ltr', width: '100%' }}>
        {data.h2_not_using_in_text.length !== 0
          ? data.h2_not_using_in_text.map((item, index) => (
              <>
                <HtmlParser html={item} />
              </>
            ))
          : '-'}
      </div>
    );
  }
};

const h3NotUsingInTextColumn: CrawlColumn = {
  field: 'h3_not_using_in_text',
  type: 'string',
  width: 270,
  gp: 'H3',
  title: 'کلمات استفاده نشده تگ H3 در متن',
  searchable: true,
  cellRenderer: params => {
    const { data } = params;
    if (!data) return null;

    return (
      <div style={{ textAlign: 'left', direction: 'ltr', width: '100%' }}>
        {data.h3_not_using_in_text.length !== 0
          ? data.h3_not_using_in_text.map((item, index) => (
              <>
                <HtmlParser html={item} />
              </>
            ))
          : '-'}
      </div>
    );
  }
};

const titleNotUsingInTextCount: CrawlColumn = {
  field: 'title_not_using_in_text_count',
  type: 'number',
  width: 300,
  title: 'تعداد کلمات استفاده نشده تگ عنوان در متن',
  searchable: true
};

const h1NotUsingInTextCountColumn: CrawlColumn = {
  field: 'h1_not_using_in_text_count',
  type: 'number',
  width: 300,
  gp: 'H1',
  wrapText: true,
  autoHeight: true,
  flex: 1,
  cellStyle: { 'white-space': 'pre' },
  title: 'تعداد کلمات استفاده نشده تگ H1 در متن',
  searchable: true,
  cellRenderer: params => (
    <div style={{ textAlign: 'left', direction: 'ltr', width: '100%' }}>
      {params.data.h1_not_using_in_text_count.length !== 0
        ? params.data.h1_not_using_in_text_count.map((item, index) => (
            <>
              {item} <br />
            </>
          ))
        : '-'}
    </div>
  )
};

const h2NotUsingInTextCountColumn: CrawlColumn = {
  field: 'h2_not_using_in_text_count',
  type: 'number',
  width: 300,
  wrapText: true,
  autoHeight: true,
  gp: 'H2',
  title: 'تعداد کلمات استفاده نشده تگ H2 در متن',
  cellStyle: { 'white-space': 'pre' },
  searchable: true,
  cellRenderer: params => (
    <div style={{ textAlign: 'left', direction: 'ltr', width: '100%' }}>
      {params.data.h2_not_using_in_text_count.length !== 0
        ? params.data.h2_not_using_in_text_count.map((item, index) => (
            <>
              {item} <br />
            </>
          ))
        : '-'}
    </div>
  )
};

const h3NotUsingInTextCountColumn: CrawlColumn = {
  field: 'h3_not_using_in_text_count',
  type: 'number',
  width: 300,
  gp: 'H3',
  wrapText: true,
  autoHeight: true,
  flex: 1,
  cellStyle: { 'white-space': 'pre' },
  title: 'تعداد کلمات استفاده نشده تگ H3 در متن',
  searchable: true,
  cellRenderer: params => (
    <div style={{ textAlign: 'left', direction: 'ltr', width: '100%' }}>
      {params.data.h3_not_using_in_text_count.length !== 0
        ? params.data.h3_not_using_in_text_count.map((item, index) => (
            <>
              {item} <br />
            </>
          ))
        : '-'}
    </div>
  )
};

const status_codeColumn: CrawlColumn = {
  field: 'status_code',
  type: 'number',
  minWidth: 130,
  title: 'کد وضعیت',
  tooltip: crawlTooltip.columns.status_code,
  searchable: true,
  cellRenderer: params => {
    const { data } = params;
    if (!data) return null;

    return <StatusCodeRenderer statusCode={data.status_code} subtle />;
  }
};

const response_timeColumn: CrawlColumn = {
  field: 'response_time',
  type: 'number',
  minWidth: 130,
  title: 'زمان پاسخ دهی (ثانیه)',
  searchable: true,
  tooltip: crawlTooltip.columns.response_time
};

const word_countColumn: CrawlColumn = {
  field: 'word_count',
  type: 'number',
  width: 170,
  title: 'تعداد کلمات صفحه',
  searchable: true,
  tooltip: crawlTooltip.columns.word_count
};

const unique_words_countColumn: CrawlColumn = {
  field: 'unique_words_count',
  type: 'number',
  width: 235,
  title: 'تعداد کلمات منحصربه‌فرد صفحه',
  searchable: true,
  tooltip: crawlTooltip.columns.unique_words_count
};

const sentence_countColumn: CrawlColumn = {
  field: 'sentence_count',
  type: 'number',
  width: 135,
  title: 'تعداد جملات',
  searchable: true,
  tooltip: crawlTooltip.columns.sentence_count
};

const length_htmlColumn: CrawlColumn = {
  field: 'length_html',
  type: 'number',
  width: 125,
  title: 'اندازه (KB)',
  searchable: true,
  tooltip: crawlTooltip.columns.length_html
};

const meta_descColumn: CrawlColumn = {
  field: 'meta_desc',
  type: 'string',
  title: 'توضیح متا',
  searchable: true,
  tooltip: crawlTooltip.columns.meta_desc
};

const meta_desc_charColumn: CrawlColumn = {
  field: 'meta_desc_char',
  type: 'number',
  width: 200,
  title: 'مقدار توضیح متا (کاراکتر)',
  searchable: true,
  tooltip: crawlTooltip.columns.meta_desc_char
};

const meta_desc_pxColumn: CrawlColumn = {
  field: 'meta_desc_px',
  type: 'number',
  width: 205,
  title: 'مقدار توضیح متا (پیکسل)',
  searchable: true,
  tooltip: crawlTooltip.columns.meta_desc_px
};

const title_charColumn: CrawlColumn = {
  field: 'title_char',
  type: 'number',
  width: 195,
  title: 'مقدار تگ عنوان (کاراکتر)',
  searchable: true,
  tooltip: crawlTooltip.columns.title_char
};

const title_pxColumn: CrawlColumn = {
  field: 'title_px',
  type: 'number',
  width: 200,
  title: 'مقدار تگ عنوان (پیکسل)',
  searchable: true,
  tooltip: crawlTooltip.columns.title_px
};

const robots_is_nofollowColumn: CrawlColumn = {
  gp: 'متا تگ robots',
  field: 'robots_is_nofollow',
  type: 'boolean',
  width: 140,
  title: 'نوفالو هست؟',
  searchable: true,
  cellRenderer: params => (
    <BadgeRenderer
      color={
        params.data.robots_is_nofollow
          ? getColor('danger')
          : getColor('gray-600')
      }
      textColor={getColor('white')}
      text={params.data.robots_is_nofollow ? 'آره' : 'نه'}
    />
  ),
  tooltip: crawlTooltip.columns.robots_is_nofollow
};
const robots_is_noindexColumn: CrawlColumn = {
  gp: 'متا تگ robots',
  field: 'robots_is_noindex',
  type: 'boolean',
  width: 160,
  title: 'نوایندکس هست؟',
  searchable: true,
  cellRenderer: params => (
    <BadgeRenderer
      color={
        params.data.robots_is_noindex
          ? getColor('danger')
          : getColor('gray-600')
      }
      textColor={getColor('white')}
      text={params.data.robots_is_noindex ? 'آره' : 'نه'}
    />
  ),
  tooltip: crawlTooltip.columns.robots_is_noindex
};

const depthColumn: CrawlColumn = {
  field: 'depth',
  type: 'number',
  width: 126,
  title: 'عمق صفحه',
  searchable: true,
  tooltip: crawlTooltip.columns.depth
};
const h1Column: CrawlColumn = {
  gp: 'تگ H1',
  field: 'h1',
  type: 'array',
  title: 'H1',
  searchable: true,
  cellRenderer: params => {
    const [, setSearchParams] = useSearchParams();
    return (
      <ArrayRenderer
        onClick={() => {
          setSearchParams({
            details: 'internal',
            activeTab: 'overview',
            page_url: params.data.url,
            job_page: params.data.job_page,
            status_code: params.data.status_code
          });
        }}
        data={params.data.h1}
      />
    );
  },
  tooltip: crawlTooltip.columns.h1
};

// const num_of_urlsColumn = {
//   field: 'num_of_urls',
//   type: 'number',
//   width: 150,
//   title: 'صفحه ارجاع',
//   searchable: true,
//   cellRenderer: params => {
//     return (
//       <div className="d-flex align-items-center gap-1  h-100">
//         <AiOutlineZoomIn
//           onClick=""
//           icon="search-plus"
//           className="cursor-pointer fs-0"
//         />
//         <span>{params.data.num_of_urls}</span>
//       </div>
//     );
//   }
//   // tooltip: crawlTooltip.columns.num_of_urls
// };

const h1_countColumn: CrawlColumn = {
  gp: 'تگ H1',
  field: 'h1_count',
  type: 'number',
  width: 110,
  title: 'تعداد H1',
  searchable: true,
  tooltip: crawlTooltip.columns.h1_count
};

const title_countColumn: CrawlColumn = {
  field: 'title_count',
  type: 'number',
  width: 155,
  title: 'تعداد تگ عنوان',
  searchable: true,
  tooltip: crawlTooltip.columns.title_count
};

const meta_descCountColumn: CrawlColumn = {
  field: 'meta_desc_count',
  type: 'number',
  width: 155,
  title: 'تعداد توضیح متا',
  searchable: true,
  tooltip: crawlTooltip.columns.meta_desc_count
};

const unique_inlink_countColumn = (
  crawl_status: number | null | undefined
): CrawlColumn => ({
  field: 'unique_inlink_count',
  type: 'number',
  width: 225,
  title: 'صفحه ارجاع (یکتا)',
  searchable: true,
  tooltip: crawlTooltip.columns.unique_inlink_count,
  cellRenderer: params => (
    <CrawlStatusChecker crawl_status={crawl_status}>
      {params.value === -1 ? <CrawlStatusError /> : params.value}
    </CrawlStatusChecker>
  )
});
const outlink_internal_countColumn = (
  crawl_status: number | null | undefined
): CrawlColumn => ({
  field: 'outlink_internal_count',
  type: 'number',
  width: 135,
  title: 'پیوند داخلی',
  searchable: true,
  cellRenderer: params => (
    <CrawlStatusChecker crawl_status={crawl_status}>
      {params.value === -1 ? <CrawlStatusError /> : params.value}
    </CrawlStatusChecker>
  ),
  tooltip: crawlTooltip.columns.outlink_internal_count
});
const unique_internal_outlink_countColumn = (
  crawl_status: number | null | undefined
): CrawlColumn => ({
  field: 'unique_internal_outlink_count',
  type: 'number',
  width: 167,
  title: 'پیوند داخلی (یکتا)',
  searchable: true,
  tooltip: crawlTooltip.columns.unique_internal_outlink_count,
  cellRenderer: params => (
    <CrawlStatusChecker crawl_status={crawl_status}>
      {params.value === -1 ? <CrawlStatusError /> : params.value}
    </CrawlStatusChecker>
  )
});
const outlink_external_countColumn = (
  crawl_status: number | null | undefined
): CrawlColumn => ({
  field: 'outlink_external_count',
  type: 'number',
  width: 175,
  title: 'پیوند خارجی',
  searchable: true,
  tooltip: crawlTooltip.columns.outlink_external_count,
  cellRenderer: params => (
    <CrawlStatusChecker crawl_status={crawl_status}>
      {params.value === -1 ? <CrawlStatusError /> : params.value}
    </CrawlStatusChecker>
  )
});
const unique_external_outlink_countColumn = (
  crawl_status: number | null | undefined
): CrawlColumn => ({
  tooltip: crawlTooltip.columns.unique_external_outlink_count,
  field: 'unique_external_outlink_count',
  type: 'number',
  width: 210,
  title: 'پیوند خارجی (یکتا)',
  searchable: true,
  cellRenderer: params => (
    <CrawlStatusChecker crawl_status={crawl_status}>
      {params.value === -1 ? <CrawlStatusError /> : params.value}
    </CrawlStatusChecker>
  )
});
const canonical_urlColumn: CrawlColumn = {
  tooltip: crawlTooltip.columns.canonical_url,
  field: 'canonical_url',
  type: 'string',
  minWidth: 180,
  title: 'نشانی کنونیکال',
  searchable: true,
  cellRenderer: params => params.value && <URLRenderer url={params.value} />
};

const canonical_url_status_codeColumn: CrawlColumn = {
  tooltip: crawlTooltip.columns.canonical_url_status_code,
  field: 'canonical_url_status_code',
  type: 'number',
  minWidth: 195,
  title: 'وضعیت نشانی کنونیکال',
  searchable: true,
  cellRenderer: params => (
    <StatusCodeRenderer
      statusCode={params.data.canonical_url_status_code}
      subtle
    />
  )
};

const redirect_toColumn: CrawlColumn = {
  // tooltip: crawlTooltip.columns.redirect_to,
  field: 'redirect_to',
  type: 'string',
  title: 'ریدایرکت به',
  searchable: true,
  cellRenderer: params => <URLRenderer url={params.data.redirect_to} />
};

const redirect_to_status_codeColumn: CrawlColumn = {
  // tooltip: crawlTooltip.columns.redirect_to_status_code,
  field: 'redirect_to_status_code',
  type: 'number',
  title: 'کد وضعیت',
  width: 120,
  searchable: true,
  cellRenderer: params => (
    <StatusCodeRenderer
      statusCode={params.data.redirect_to_status_code}
      subtle
    />
  )
};

const historical_redirectColumn: CrawlColumn = {
  // tooltip: crawlTooltip.columns.historical_redirect,
  field: 'historical_redirect',
  title: 'مسیر ریدایرکت ها',
  wrapText: true,
  autoHeight: true,
  flex: 1,
  cellStyle: { 'white-space': 'pre' },
  searchable: true,
  cellRenderer: params => (
    <div style={{ textAlign: 'left', direction: 'ltr', width: '100%' }}>
      {params.data.historical_redirect.length !== 0
        ? params.data.historical_redirect.map((item, index) => (
            <div key={index}>
              <StatusCodeRenderer
                statusCode={params.data.historical_redirect_status[index]}
                subtle
              />
              <URLRenderer url={item} enableCopyAndRedirect={false} />
              {index !== params.data.historical_redirect.length - 1 && (
                <FiCornerRightDown className="me-1" />
              )}
            </div>
          ))
        : '-'}
    </div>
  )
};

const canonical_url_multipleColumn: CrawlColumn = {
  field: 'canonical_url_multiple',
  title: 'تگ های کنونیکال',
  wrapText: true,
  autoHeight: true,
  flex: 1,
  cellStyle: { 'white-space': 'pre' },
  searchable: true,
  cellRenderer: params => (
    <div style={{ textAlign: 'left', direction: 'ltr', width: '100%' }}>
      {params.data.canonical_url_multiple.length !== 0
        ? params.data.canonical_url_multiple.map((item, index) => (
            <>
              <URLRenderer url={item} enableCopyAndRedirect={false} /> <br />
            </>
          ))
        : '-'}
    </div>
  )
};

const altColumn: CrawlColumn = {
  // tooltip: crawlTooltip.columns.alt,
  field: 'alt',
  type: 'string',
  title: 'alt'
};
const protocolColumn: CrawlColumn = {
  tooltip: crawlTooltip.columns.protocol,
  field: 'protocol',
  type: 'string',
  width: 120,
  title: 'پروتکل',
  cellRenderer: params => <ProtocolRenderer protocol={params.data.protocol} />
};

// CATEGORIES
export const checkListConfig = (
  crawl_status: number | null | undefined = null
): CheckListConfig => {
  return {
    //=======================ERRORS==================/
    errors: [
      {
        title: 'is_4xx',
        show_title: 'خطای 4xx',
        link: '4xx_error',
        mode: 'error',
        gp: 'خطای xxx',
        count: 13,
        priority: 'high',
        columns: [
          urlColumn(), //✔️
          status_codeColumn,
          typeColumn,
          {
            field: 'inlink_count',
            tooltip: crawlTooltip.columns.inlink_count,
            type: 'number',
            minWidth: 150,
            title: 'صفحه ارجاع',
            cellRenderer: params => {
              const [, setSearchParams] = useSearchParams();
              const { data } = params;
              if (!data) return null;

              return (
                <CrawlStatusChecker crawl_status={crawl_status}>
                  {params.value === -1 ? (
                    <CrawlStatusError />
                  ) : (
                    <div className="d-flex align-items-center gap-1 h-100">
                      <AiOutlineZoomIn
                        onClick={() => {
                          const searchParams = {
                            issues: '4xx_error',
                            details: typeToDetail(params.data.type),
                            activeTab: typeToDetail(params.data.type),
                            job_page: params.data.job_page,
                            type: params.data.type
                          };
                          typeToDetail(params.data.type) === 'assets_all'
                            ? (searchParams.asset_url = decodeURIComponent(
                                params.data.url
                              ))
                            : (searchParams.page_url = decodeURIComponent(
                                params.data.url
                              ));
                          setSearchParams(searchParams);
                        }}
                        className="cursor-pointer fs-0"
                      />
                      <span>{params.data.inlink_count}</span>
                    </div>
                  )}
                </CrawlStatusChecker>
              );
            }
          }
        ]
      },
      {
        title: 'is_5xx',
        show_title: 'خطای 5xx',
        link: '5xx_error',
        mode: 'error',
        gp: 'خطای xxx',
        priority: 'high',
        count: 13,
        columns: [
          urlColumn(), //✔️
          status_codeColumn,
          typeColumn,
          {
            field: 'inlink_count',
            tooltip: crawlTooltip.columns.inlink_count,
            type: 'number',
            minWidth: 150,
            title: 'صفحه ارجاع',
            cellRenderer: params => {
              const [, setSearchParams] = useSearchParams();
              return (
                <CrawlStatusChecker crawl_status={crawl_status}>
                  {params.value === -1 ? (
                    <CrawlStatusError />
                  ) : (
                    <div className="d-flex align-items-center gap-1  h-100">
                      <AiOutlineZoomIn
                        onClick={() => {
                          const searchParams = {
                            issues: '5xx_error',
                            details: typeToDetail(params.data.type),
                            activeTab: typeToDetail(params.data.type),
                            job_page: params.data.job_page,
                            type: params.data.type
                          };
                          typeToDetail(params.data.type) === 'assets_all'
                            ? (searchParams.asset_url = decodeURIComponent(
                                params.data.url
                              ))
                            : (searchParams.page_url = decodeURIComponent(
                                params.data.url
                              ));
                          setSearchParams(searchParams);
                        }}
                        className="cursor-pointer fs-0"
                      />
                      <span>{params.data.inlink_count}</span>
                    </div>
                  )}
                </CrawlStatusChecker>
              );
            }
          }
        ]
      },
      {
        title: 'title_not_using',
        show_title: 'تگ عنوان ندارد',
        mode: 'error',
        gp: 'عنوان',
        link: 'dont_have_title',
        count: 13,
        priority: 'high',
        columns: [
          urlColumn(), //✔️
          status_codeColumn,
          robots_is_nofollowColumn,
          robots_is_noindexColumn,
          depthColumn
        ]
      },
      {
        title: 'title_is_duplicate',
        mode: 'error',
        gp: 'عنوان',
        show_title: 'تگ عنوان تکراری',
        link: 'duplicate_title',
        count: 13,
        priority: 'high',
        columns: [
          ps_urlColumn(), //✔️
          titleColumn,
          title_charColumn,
          title_pxColumn
        ]
      },
      {
        title: 'title_several_using',
        link: 'multiple_title',
        mode: 'error',
        gp: 'عنوان',
        show_title: 'چندین تگ عنوان',
        count: 13,
        priority: 'high',
        columns: [
          urlColumn(), //❓
          titleColumn,
          title_countColumn,
          robots_is_nofollowColumn,
          robots_is_noindexColumn
        ]
      },
      {
        title: 'meta_desc_several_using',
        link: 'meta_desc_several_using',
        mode: 'error',
        gp: 'متا',
        show_title: 'چندین توضیح متا',
        count: 13,
        priority: 'high',
        columns: [
          urlColumn(), //✔️
          meta_descColumn,
          meta_descCountColumn,
          robots_is_nofollowColumn,
          robots_is_noindexColumn
        ]
      },
      {
        title: 'robots_is_multiple',
        mode: 'error',
        gp: 'متا',
        link: 'robots_is_multiple',
        show_title: 'چندین متاتگ robots',
        count: 13,
        priority: 'high',
        columns: [
          urlColumn(), //✔️
          robots_is_nofollowColumn,
          robots_is_noindexColumn
        ]
      },
      {
        title: 'canonical_url_status_code_bad',
        mode: 'error',
        gp: 'کنونیکال',
        link: 'canonical_url_status_code_bad',
        show_title: 'صفحه با کنونیکال مشکل دار',
        count: 13,
        priority: 'high',
        columns: [
          urlColumn(), //✔️
          canonical_urlColumn,
          canonical_url_status_codeColumn
        ]
      },
      {
        title: 'canonical_url_multiple',
        mode: 'error',
        gp: 'کنونیکال',
        link: 'canonical_is_multiple',
        show_title: 'چندین تگ کنونیکال',
        count: 13,
        priority: 'high',
        columns: [
          urlColumn(), //❓
          titleColumn,
          status_codeColumn,
          canonical_url_multipleColumn
        ]
      },
      {
        title: 'title_char_is_short',
        mode: 'error',
        gp: 'عنوان',
        show_title: 'تگ عنوان کوتاه (کاراکتر)',
        link: 'short_char_title',
        priority: 'medium',
        count: 13,
        columns: [
          urlColumn(), //✔️
          titleColumn,
          title_charColumn,
          title_pxColumn
        ]
      },
      {
        title: 'title_px_is_short',
        show_title: 'تگ عنوان کوتاه (پیکسل)',
        mode: 'error',
        gp: 'عنوان',
        priority: 'medium',
        link: 'short_px_title',
        count: 13,
        columns: [
          urlColumn(), //✔️
          titleColumn,
          title_charColumn,
          title_pxColumn
        ]
      },
      {
        title: 'title_not_using_in_text',
        link: 'title_not_using_in_text',
        mode: 'error',
        gp: 'عنوان',
        show_title: 'کلمات استفاده نشده تگ عنوان در متن',
        count: 13,
        priority: 'medium',
        columns: [
          urlColumn(), //✔️
          status_codeColumn,
          titleNotUsingInTextColumn,
          titleNotUsingInTextCount
        ]
      },
      {
        title: 'title_have_archive',
        mode: 'error',
        gp: 'عنوان',
        link: 'archive_error',
        show_title: 'خطای بایگانی در تگ عنوان',
        count: 13,
        priority: 'medium',
        columns: [
          urlColumn(), //❓
          titleColumn
        ]
      },
      {
        title: 'old_blog',
        mode: 'error',
        gp: 'صفحات',
        link: 'old_pages',
        show_title: 'صفحات با محتوای خیلی قدیمی',
        count: 13,
        priority: 'medium',
        columns: [
          urlColumn(), //✔️
          titleColumn,
          lastMdifiedByDay,
          lastModified,
          publishedTime
        ]
      },
      {
        title: 'low_word',
        mode: 'error',
        gp: 'محتوا',
        show_title: 'صفحات با محتوای کم',
        link: 'low_word',
        priority: 'medium',
        count: 13,
        columns: [
          urlColumnWithoutFlex(),
          status_codeColumn,
          titleColumn,
          word_countColumn,
          unique_words_countColumn,
          sentence_countColumn,
          robots_is_noindexColumn
        ]
      },
      {
        title: 'link_unknown',
        link: 'link_unknown',
        mode: 'error',
        gp: 'پیوند',
        show_title: 'پیوندهای ناشناخته',
        count: 13,
        priority: 'medium',
        columns: [
          page_link_typeColumn,
          ps_urlColumn(), //✔️
          link_textColumn,
          unknown_urlColumn
        ]
      },
      {
        title: 'redirect_is_loop',
        link: 'redirect_is_loop',
        mode: 'error',
        gp: 'ریدایرکت',
        show_title: 'ریدایرکت حلقه‌ای',
        count: 13,
        priority: 'medium',
        columns: [
          historical_redirectColumn,
          {
            field: 'inlink_count',
            tooltip: crawlTooltip.columns.inlink_count,
            type: 'number',
            minWidth: 150,
            title: 'صفحه ارجاع',
            cellRenderer: params => {
              const [, setSearchParams] = useSearchParams();
              return (
                <CrawlStatusChecker crawl_status={crawl_status}>
                  {params.value === -1 ? (
                    <CrawlStatusError />
                  ) : (
                    <div className="d-flex align-items-center gap-1 h-100">
                      <AiOutlineZoomIn
                        onClick={() =>
                          setSearchParams({
                            issues: 'redirect_is_loop',
                            details: 'links',
                            activeTab: 'links',
                            page_url: decodeURIComponent(params.data.url),
                            job_page: params.data.job_page,
                            type: 'internal'
                          })
                        }
                        className="cursor-pointer fs-0"
                      />
                      <span>{params.data.inlink_count}</span>
                    </div>
                  )}
                </CrawlStatusChecker>
              );
            }
          }
        ]
      },
      {
        title: 'redirect_is_chain',
        mode: 'error',
        gp: 'ریدایرکت',
        link: 'redirect_is_chain',
        show_title: 'ریدایرکت پیوسته',
        priority: 'low',
        count: 13,
        columns: [
          historical_redirectColumn,
          {
            field: 'inlink_count',
            tooltip: crawlTooltip.columns.inlink_count,
            type: 'number',
            minWidth: 150,
            title: 'صفحه ارجاع',
            cellRenderer: params => {
              const [, setSearchParams] = useSearchParams();
              return (
                <CrawlStatusChecker crawl_status={crawl_status}>
                  {params.value === -1 ? (
                    <CrawlStatusError />
                  ) : (
                    <div className="d-flex align-items-center gap-1  h-100">
                      <AiOutlineZoomIn
                        onClick={() =>
                          setSearchParams({
                            issues: 'redirect_is_chain',
                            details: 'links',
                            activeTab: 'links',
                            page_url: decodeURIComponent(params.data.url),
                            job_page: params.data.job_page,
                            type: 'internal'
                          })
                        }
                        className="cursor-pointer fs-0"
                      />
                      <span>{params.data.inlink_count}</span>
                    </div>
                  )}
                </CrawlStatusChecker>
              );
            }
          }
        ]
      },
      {
        title: 'without_alt_image',
        mode: 'error',
        gp: 'محتوا',
        link: 'images_without_alt',
        show_title: 'تصاویر بدون متن جایگرین (alt)',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn({
            //✔️
            issues: 'images_without_alt',
            details: 'assets_all',
            activeTab: 'assets_all',
            type: 'image'
          }),
          {
            field: 'inlink_count',
            type: 'number',
            minWidth: 150,
            title: 'صفحه ارجاع',
            tooltip: crawlTooltip.columns.inlink_count,
            cellRenderer: params => {
              const [, setSearchParams] = useSearchParams();
              const { data } = params;
              if (!data) return null;

              return (
                <CrawlStatusChecker crawl_status={crawl_status}>
                  {params.value === -1 ? (
                    <CrawlStatusError />
                  ) : (
                    <div className="d-flex align-items-center gap-1  h-100">
                      <AiOutlineZoomIn
                        onClick={() =>
                          setSearchParams({
                            issues: 'images_without_alt',
                            details: 'assets_all',
                            activeTab: 'assets_all',
                            asset_url: decodeURIComponent(params.data.url),
                            job_page: params.data.job_page,
                            type: 'image'
                          })
                        }
                        className="cursor-pointer fs-0"
                      />
                      <span>{params.data.inlink_count}</span>
                    </div>
                  )}
                </CrawlStatusChecker>
              );
            }
          }
        ]
      },
      {
        title: 'uri_issue_over_limit',
        mode: 'error',
        gp: 'نشانی',
        link: 'uri_issue_over_limit',
        show_title: 'نشانی طولانی',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //❓
          titleColumn,
          status_codeColumn
        ]
      },
      {
        title: 'uri_issue_many_paths',
        link: 'uri_issue_many_paths',
        gp: 'نشانی',
        mode: 'error',
        show_title: 'نشانی با فولدر زیاد',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          titleColumn,
          status_codeColumn
        ]
      }
    ],
    //=======================WARNINGS==================/
    warning: [
      {
        title: 'is_low_unique_inlink',
        mode: 'warning',
        gp: 'صفحات',
        link: 'is_low_unique_inlink',
        show_title: 'صفحات تنها',
        count: 13,
        priority: 'high',
        columns: [
          urlColumn({
            //✔️
            issues: 'is_low_unique_inlink',
            details: 'links',
            activeTab: 'links',
            type: 'internal'
          }),
          {
            field: 'inlink_count',
            type: 'number',
            tooltip: crawlTooltip.columns.inlink_count,
            minWidth: 150,
            title: 'صفحه ارجاع',
            cellRenderer: params => {
              const [, setSearchParams] = useSearchParams();

              return (
                <CrawlStatusChecker crawl_status={crawl_status}>
                  {params.value === -1 ? (
                    <CrawlStatusError />
                  ) : (
                    <div className="d-flex align-items-center gap-1  h-100">
                      <AiOutlineZoomIn
                        onClick={() =>
                          setSearchParams({
                            issues: 'is_low_unique_inlink',
                            details: 'links',
                            activeTab: 'links',
                            page_url: decodeURIComponent(params.data.url),
                            job_page: params.data.job_page,
                            type: 'internal'
                          })
                        }
                        className="cursor-pointer fs-0"
                      />
                      <span>{params.data.inlink_count}</span>
                    </div>
                  )}
                </CrawlStatusChecker>
              );
            }
          },
          unique_inlink_countColumn(crawl_status)
        ]
      },
      {
        title: 'is_low_internal_outlinks',
        link: 'is_low_internal_outlinks',
        mode: 'warning',
        gp: 'صفحات',
        show_title: 'صفحات با لینک داخلی کم',
        count: 13,
        priority: 'high',
        columns: [
          urlColumn(), //❓
          outlink_internal_countColumn(crawl_status),
          unique_internal_outlink_countColumn(crawl_status)
        ]
      },
      {
        title: 'link_text_bad',
        mode: 'warning',
        gp: 'پیوند',
        link: 'link_text_bad',
        show_title: 'متن پیوند بد',
        count: 13,
        priority: 'medium',
        columns: [
          ps_urlColumn(),
          link_textColumn,
          link_hrefColumn(), //❌
          link_is_nofollowColumn
        ]
      },
      {
        title: 'link_is_nofollow',
        link: 'link_is_nofollow',
        mode: 'warning',
        gp: 'پیوند',
        show_title: 'پیوندهای داخلی نوفالو',
        count: 13,
        priority: 'medium',
        columns: [link_hrefColumn(), ps_urlColumn()]
      },
      {
        title: 'bad_response_time',
        link: 'bad_response_time',
        mode: 'warning',
        gp: 'صفحات',
        show_title: 'پاسخ دهی کند',
        count: 13,
        priority: 'medium',
        columns: [
          urlColumn(), //✔️
          status_codeColumn,
          response_timeColumn
        ]
      },
      {
        title: 'h1_not_using',
        mode: 'warning',
        gp: 'h1-h6',
        link: 'dont_have_h1',
        show_title: 'تگ H1 ندارد',
        count: 13,
        priority: 'medium',
        columns: [
          urlColumn(), //✔️
          titleColumn,
          status_codeColumn
        ]
      },
      {
        title: 'meta_desc_not_using',
        mode: 'warning',
        gp: 'متا',
        link: 'dont_have_meta',
        show_title: 'توضیح متا ندارد',
        count: 13,
        priority: 'medium',
        columns: [
          urlColumn(), //✔️
          status_codeColumn,
          robots_is_nofollowColumn,
          robots_is_noindexColumn
        ]
      },
      {
        title: 'title_most_repeated_word',
        mode: 'warning',
        gp: 'عنوان',
        link: 'most_repetition_of_words_in_title',
        show_title: 'تکرار اضافه در تگ عنوان',
        count: 13,
        priority: 'medium',
        columns: [
          urlColumn(), //✔️
          titleColumn,
          status_codeColumn
        ]
      },
      // {
      //   title: 'similar_titles',
      //   mode: 'warning',
      //   gp: 'عنوان',
      //   link: 'similar_titles',
      //   show_title: 'تگ عنوان‌ مشابه',
      //   count: 13,
      //   priority: 'low',
      //   columns: [similarTitleColumn] //FIXME
      // },
      {
        title: 'title_char_is_long',
        mode: 'warning',
        gp: 'عنوان',
        link: 'long_char_title',
        show_title: 'تگ عنوان طولانی (کاراکتر)',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          titleColumn,
          title_charColumn,
          title_pxColumn
        ]
      },
      {
        title: 'title_px_is_long',
        mode: 'warning',
        gp: 'عنوان',
        link: 'long_px_title',
        show_title: 'تگ عنوان طولانی (پیکسل)',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          titleColumn,
          title_charColumn,
          title_pxColumn
        ]
      },
      {
        title: 'high_word',
        mode: 'warning',
        gp: 'صفحات',
        link: 'high_word',
        show_title: 'صفحات با محتوای خیلی زیاد',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          titleColumn,
          word_countColumn,
          unique_words_countColumn,
          sentence_countColumn,
          robots_is_noindexColumn
        ]
      },
      {
        title: 'page_size_high',
        mode: 'warning',
        gp: 'صفحات',
        link: 'page_size_high',
        show_title: 'صفحات با حجم بالا',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          status_codeColumn,
          titleColumn,
          length_htmlColumn
        ]
      },
      {
        title: 'image_size_high',
        mode: 'warning',
        gp: 'محتوا',
        link: 'images_with_high_size',
        show_title: 'تصاویر با حجم بالا',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //❌
          status_codeColumn,
          sizeKBColumn,
          {
            field: 'inlink_count',
            tooltip: crawlTooltip.columns.inlink_count,
            type: 'number',
            minWidth: 150,
            title: 'صفحه ارجاع',
            cellRenderer: params => {
              //❌
              const [, setSearchParams] = useSearchParams();
              return (
                <CrawlStatusChecker crawl_status={crawl_status}>
                  {params.value === -1 ? (
                    <CrawlStatusError />
                  ) : (
                    <div className="d-flex align-items-center gap-1  h-100">
                      <AiOutlineZoomIn
                        onClick={() => {
                          const searchParams = {
                            issues: 'images_with_high_size',
                            details: typeToDetail(params.data.type),
                            activeTab: 'links',
                            job_page: params.data.job_page,
                            type: params.data.type
                          };
                          typeToDetail(params.data.type) === 'assets_all'
                            ? (searchParams.asset_url = params.data.url)
                            : (searchParams.page_url = params.data.url);
                          setSearchParams(searchParams);
                        }}
                        className="cursor-pointer fs-0"
                      />
                      <span>{params.data.inlink_count}</span>
                    </div>
                  )}
                </CrawlStatusChecker>
              );
            }
          }
        ]
      },
      {
        title: 'meta_desc_px_is_short',
        mode: 'warning',
        gp: 'متا',
        link: 'short_px_meta',
        show_title: 'توضیح متا کوتاه‌ (پیکسل)',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          meta_descColumn,
          meta_desc_charColumn,
          meta_desc_pxColumn
        ]
      },

      {
        title: 'meta_desc_char_is_short',
        mode: 'warning',
        gp: 'متا',
        link: 'short_char_meta',
        show_title: 'توضیح متا کوتاه (کارکتر) ',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          meta_descColumn,
          meta_desc_charColumn,
          meta_desc_pxColumn
        ]
      },

      {
        title: 'meta_desc_is_duplicate',
        link: 'duplicate_meta',
        mode: 'warning',
        gp: 'متا',
        show_title: 'توضیح متا تکراری',
        count: 13,
        priority: 'low',
        columns: [
          ps_urlColumn(), //✔️
          meta_descColumn,
          meta_desc_charColumn,
          meta_desc_pxColumn
        ]
      },
      {
        title: 'h1_count',
        link: 'multiple_h1',
        mode: 'warning',
        gp: 'h1-h6',
        show_title: 'چندین تگ H1',
        priority: 'low',
        count: 13,
        columns: [
          urlColumn(), //✔️
          h1Column,
          h1_countColumn,
          robots_is_nofollowColumn,
          robots_is_noindexColumn
        ]
      },

      {
        title: 'assets_high_size',
        mode: 'warning',
        gp: 'منابع',
        link: 'assets_with_high_size',
        show_title: 'منابع با حجم بالا',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //❓
          status_codeColumn,
          sizeKBColumn,
          {
            field: 'inlink_count',
            type: 'number',
            minWidth: 150,
            title: 'صفحه ارجاع',
            tooltip: crawlTooltip.columns.inlink_count,
            cellRenderer: params => {
              const [, setSearchParams] = useSearchParams();
              return (
                <CrawlStatusChecker crawl_status={crawl_status}>
                  {params.value === -1 ? (
                    <CrawlStatusError />
                  ) : (
                    <div className="d-flex align-items-center gap-1  h-100">
                      <AiOutlineZoomIn
                        onClick={() => {
                          const searchParams = {
                            issues: 'assets_with_high_size',
                            details: typeToDetail(params.data.type),
                            activeTab: typeToDetail(params.data.type),
                            job_page: params.data.job_page,
                            type: params.data.type
                          };
                          typeToDetail(params.data.type) === 'assets_all'
                            ? (searchParams.asset_url = params.data.url)
                            : (searchParams.page_url = params.data.url);
                          setSearchParams(searchParams);
                        }}
                        className="cursor-pointer fs-0"
                      />
                      <span>{params.data.inlink_count}</span>
                    </div>
                  )}
                </CrawlStatusChecker>
              );
            }
          }
        ]
      },

      {
        title: 'is_page_high_unique_external_outline',
        link: 'is_page_high_unique_external_outline',
        mode: 'warning',
        gp: 'صفحات',
        show_title: 'صفحات با لینک خارجی زیاد',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          outlink_internal_countColumn(crawl_status),
          unique_internal_outlink_countColumn(crawl_status),
          outlink_external_countColumn(crawl_status),
          unique_external_outlink_countColumn(crawl_status)
        ]
      },

      {
        title: 'is_depth_high',
        link: 'high_depth',
        show_title: 'صفحات با عمق زیاد',
        mode: 'warning',
        gp: 'صفحات',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          status_codeColumn,
          titleColumn,
          depthColumn
        ]
      },
      {
        title: 'uri_issue_whitespace',
        link: 'uri_issue_whitespace',
        mode: 'warning',
        gp: 'نشانی',
        show_title: 'نشانی دارای اسپیس',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //❓
          titleColumn,
          status_codeColumn
        ]
      },
      {
        title: 'uri_issue_multiple_slash',
        mode: 'warning',
        gp: 'نشانی',
        link: 'uri_issue_multiple_slash',
        show_title: 'نشانی با چندین اسلش',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //❓
          titleColumn,
          status_codeColumn
        ]
      },
      {
        title: 'uri_issue_repetitive',
        mode: 'warning',
        gp: 'نشانی',
        link: 'uri_issue_repetitive',
        show_title: 'نشانی با فولدر تکراری',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          titleColumn,
          status_codeColumn
        ]
      },
      {
        title: 'h1_not_using_in_text_bool',
        link: 'h1_not_using_in_text',
        mode: 'warning',
        gp: 'h1-h6',
        show_title: 'کلمات استفاده نشده تگ H1 در متن',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          h1NotUsingInTextColumn,
          h1NotUsingInTextCountColumn
        ]
      }
    ],
    //=======================INFO==================/
    info: [
      {
        title: 'robots_is_noindex',
        mode: 'info',
        gp: 'صفحات',
        link: 'no_index',
        show_title: 'صفحات نوایندکس',
        count: 13,
        priority: 'high',
        columns: [
          urlColumn(), //✔️
          titleColumn,
          status_codeColumn,
          robots_is_noindexColumn,
          robots_is_nofollowColumn
        ]
      },

      {
        title: 'robots_is_nofollow',
        link: 'no_follow',
        mode: 'info',
        gp: 'صفحات',
        show_title: 'صفحات نوفالو',
        count: 13,
        priority: 'high',
        columns: [
          urlColumn(), //✔️
          titleColumn,
          status_codeColumn,
          robots_is_nofollowColumn,
          robots_is_noindexColumn
        ]
      },
      {
        title: 'canonical_is_not_itself',
        link: 'canonical_other_than_itself',
        mode: 'info',
        gp: 'کنونیکال',
        show_title: 'کنونیکال به غیر',
        count: 13,
        priority: 'high',
        columns: [
          urlColumn(), //✔️
          titleColumn,
          status_codeColumn,
          canonical_urlColumn,
          canonical_url_status_codeColumn
        ]
      },

      {
        title: 'redirect',
        link: 'redirect',
        show_title: 'ریدایرکت 3XX',
        mode: 'info',
        gp: 'ریدایرکت',
        count: 13,
        priority: 'medium',
        columns: [
          urlColumn({
            //✔️
            issues: 'redirect',
            details: 'links',
            activeTab: 'links',
            type: 'internal'
          }),
          redirect_to_status_codeColumn,
          redirect_toColumn,
          historical_redirectColumn,
          {
            field: 'inlink_count',
            type: 'number',
            width: 130,
            tooltip: crawlTooltip.columns.inlink_count,
            title: 'صفحه ارجاع',
            cellRenderer: params => {
              const [, setSearchParams] = useSearchParams();
              return (
                <CrawlStatusChecker crawl_status={crawl_status}>
                  {params.value === -1 ? (
                    <CrawlStatusError />
                  ) : (
                    <div className="d-flex align-items-center gap-1  h-100">
                      <AiOutlineZoomIn
                        onClick={() =>
                          setSearchParams({
                            issues: 'redirect',
                            details: 'links',
                            activeTab: 'links',
                            page_url: decodeURIComponent(params.data.url),
                            job_page: params.data.job_page,
                            type: 'internal'
                          })
                        }
                        className="cursor-pointer fs-0"
                      />
                      <span>{params.data.inlink_count}</span>
                    </div>
                  )}
                </CrawlStatusChecker>
              );
            }
          }
        ]
      },
      {
        title: 'h2_not_using',
        link: 'dont_have_h2',
        mode: 'info',
        gp: 'h1-h6',
        show_title: 'تگ H2 ندارد',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          status_codeColumn,
          titleColumn
        ]
      },
      {
        title: 'h3_not_using',
        mode: 'info',
        gp: 'h1-h6',
        link: 'dont_have_h3',
        show_title: 'تگ H3 ندارد',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          status_codeColumn,
          titleColumn
        ]
      },
      // {
      //   title: 'similar_paragraphs',
      //   mode: 'info',
      //   gp: 'محتوا',
      //   link: 'similar_paragraphs',
      //   show_title: 'پاراگراف مشابه',
      //   count: 13,
      //   priority: 'low',
      //   columns: [similarTitleColumn] //FIXME
      // },
      {
        title: 'meta_desc_char_is_long',
        link: 'long_char_meta',
        mode: 'info',
        gp: 'متا',
        show_title: 'توضیح متا طولانی (کاراکتر)',
        priority: 'low',
        count: 13,
        columns: [
          urlColumn(), //✔️
          meta_descColumn,
          meta_desc_charColumn,
          meta_desc_pxColumn
        ]
      },
      {
        title: 'meta_desc_px_is_long',
        mode: 'info',
        gp: 'متا',
        link: 'long_px_meta',
        show_title: 'توضیح متا طولانی (پیکسل)',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          meta_descColumn,
          meta_desc_charColumn,
          meta_desc_pxColumn
        ]
      },
      {
        title: 'title_have_emoji',
        mode: 'info',
        gp: 'عنوان',
        link: 'emoji_in_title',
        show_title: 'تگ عنوان با ایموجی',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          titleColumn,
          status_codeColumn
        ]
      },
      {
        title: 'query_count_high',
        mode: 'info',
        gp: 'نشانی',
        link: 'many_query_in_url',
        show_title: 'تعداد کوئری زیاد در نشانی',
        count: 13,
        priority: 'low',
        columns: [
          ps_urlColumn(), //❓
          link_hrefColumn(),
          link_textColumn
        ]
      },
      {
        title: 'internal',
        link: 'internal',
        mode: 'info',
        gp: 'پیوند',
        show_title: 'پیوند داخلی',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn({
            //✔️
            issues: 'internal',
            details: 'internal',
            activeTab: 'overview',
            type: 'internal'
          }),
          status_codeColumn,
          {
            field: 'inlink_count',
            tooltip: crawlTooltip.columns.inlink_count,
            type: 'number',
            width: 130,
            title: 'صفحه ارجاع',
            cellRenderer: params => {
              const [, setSearchParams] = useSearchParams();

              return (
                <CrawlStatusChecker crawl_status={crawl_status}>
                  {params.value === -1 ? (
                    <CrawlStatusError />
                  ) : (
                    <div className="d-flex align-items-center gap-1  h-100">
                      <AiOutlineZoomIn
                        onClick={() =>
                          setSearchParams({
                            issues: 'internal',
                            details: 'internal',
                            activeTab: 'links',
                            page_url: decodeURIComponent(params.data.url),
                            job_page: params.data.job_page,
                            type: 'internal'
                          })
                        }
                        className="cursor-pointer fs-0"
                      />
                      <span>{params.data.inlink_count}</span>
                    </div>
                  )}
                </CrawlStatusChecker>
              );
            }
          }
        ]
      },
      {
        title: 'external',
        link: 'external',
        show_title: 'پیوند خارجی',
        count: 13,
        mode: 'info',
        gp: 'پیوند',
        priority: 'low',
        columns: [
          urlColumn({
            //✔️
            issues: 'external',
            details: 'links',
            type: 'external',
            activeTab: 'links'
          }),
          status_codeColumn,
          {
            field: 'inlink_count',
            type: 'number',
            width: 130,
            title: 'صفحه ارجاع',
            cellRenderer: params => {
              const [, setSearchParams] = useSearchParams();
              const { data } = params;
              if (!data) return null;

              return (
                <CrawlStatusChecker crawl_status={crawl_status}>
                  {params.value === -1 ? (
                    <CrawlStatusError />
                  ) : (
                    <div className="d-flex align-items-center gap-1  h-100">
                      <AiOutlineZoomIn
                        onClick={() =>
                          setSearchParams({
                            issues: 'external',
                            details: 'links',
                            activeTab: 'links',
                            page_url: decodeURIComponent(params.data.url),
                            job_page: params.data.job_page,
                            type: 'external'
                          })
                        }
                        className="cursor-pointer fs-0"
                      />
                      <span>{params.data.inlink_count}</span>
                    </div>
                  )}
                </CrawlStatusChecker>
              );
            }
          }
        ]
      },
      {
        title: 'image',
        mode: 'info',
        gp: 'محتوا',

        link: 'image',
        show_title: 'تصاویر',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn({
            //✔️
            issues: 'image',
            details: 'assets_all',
            activeTab: 'assets_all',
            type: 'image'
          }),
          status_codeColumn,
          {
            field: 'inlink_count',
            type: 'number',
            width: 130,
            title: 'صفحه ارجاع',
            cellRenderer: params => {
              const [, setSearchParams] = useSearchParams();
              const { data } = params;
              if (!data) return null;

              return (
                <CrawlStatusChecker crawl_status={crawl_status}>
                  {params.value === -1 ? (
                    <CrawlStatusError />
                  ) : (
                    <div className="d-flex align-items-center gap-1  h-100">
                      <AiOutlineZoomIn
                        onClick={() =>
                          setSearchParams({
                            issues: 'image',
                            details: 'assets_all',
                            activeTab: 'assets_all',
                            asset_url: decodeURIComponent(data.url),
                            job_page: data.job_page,
                            type: 'image'
                          })
                        }
                        className="cursor-pointer fs-0"
                      />
                      <span>{data.inlink_count}</span>
                    </div>
                  )}
                </CrawlStatusChecker>
              );
            }
          }
        ]
      },
      {
        title: 'css',
        link: 'css',
        mode: 'info',
        gp: 'منابع',

        show_title: 'فایل های CSS',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn({
            //✔️
            issues: 'css',
            details: 'assets_all',
            activeTab: 'assets_all',
            type: 'css'
          }),
          status_codeColumn,
          {
            field: 'inlink_count',
            type: 'number',
            minWidth: 150,
            title: 'صفحه ارجاع',
            tooltip: crawlTooltip.columns.inlink_count,
            cellRenderer: params => {
              const [, setSearchParams] = useSearchParams();
              const { data } = params;
              if (!data) return null;

              return (
                <CrawlStatusChecker crawl_status={crawl_status}>
                  {params.value === -1 ? (
                    <CrawlStatusError />
                  ) : (
                    <div className="d-flex align-items-center gap-1  h-100">
                      <AiOutlineZoomIn
                        onClick={() =>
                          setSearchParams({
                            issues: 'css',
                            details: 'assets_all',
                            activeTab: 'assets_all',
                            asset_url: decodeURIComponent(data.url),
                            job_page: data.job_page,
                            type: data.type
                          })
                        }
                        className="cursor-pointer fs-0"
                      />
                      <span>{data.inlink_count}</span>
                    </div>
                  )}
                </CrawlStatusChecker>
              );
            }
          }
        ]
      },
      {
        title: 'js', //✔️
        link: 'js',
        mode: 'info',
        gp: 'منابع',
        show_title: 'فایل های JS',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn({
            issues: 'js',
            details: 'assets_all',
            activeTab: 'assets_all',
            type: 'js'
          }),
          status_codeColumn,
          {
            field: 'inlink_count',
            tooltip: crawlTooltip.columns.inlink_count,
            type: 'number',
            minWidth: 150,
            title: 'صفحه ارجاع',
            cellRenderer: params => {
              const [, setSearchParams] = useSearchParams();
              const { data } = params;
              if (!data) return null;

              return (
                <CrawlStatusChecker crawl_status={crawl_status}>
                  {params.value === -1 ? (
                    <CrawlStatusError />
                  ) : (
                    <div className="d-flex align-items-center gap-1  h-100">
                      <AiOutlineZoomIn
                        onClick={() =>
                          setSearchParams({
                            issues: 'js',
                            details: 'assets_all',
                            activeTab: 'assets_all',
                            asset_url: decodeURIComponent(data.url),
                            job_page: data.job_page,
                            type: data.type
                          })
                        }
                        className="cursor-pointer fs-0"
                      />
                      <span>{data.inlink_count}</span>
                    </div>
                  )}
                </CrawlStatusChecker>
              );
            }
          }
        ]
      },
      {
        title: 'uri_issue_underscore',
        link: 'uri_issue_underscore',
        mode: 'info',
        gp: 'نشانی',
        show_title: 'نشانی دارای آندراسکور',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //❓
          titleColumn,
          status_codeColumn
        ]
      },
      {
        title: 'uri_issue_uppercase',
        link: 'uri_issue_uppercase',
        mode: 'info',
        gp: 'نشانی',
        show_title: 'نشانی با حروف بزرگ', //✔️
        count: 13,
        priority: 'low',
        columns: [urlColumn(), titleColumn, status_codeColumn]
      },
      {
        title: 'uri_issue_query_parameters',
        link: 'uri_issue_query_parameters',
        mode: 'info',
        gp: 'نشانی',
        show_title: 'نشانی با کوئری',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //❓
          titleColumn,
          status_codeColumn
        ]
      },
      {
        title: 'h2_not_using_in_text_bool',
        link: 'h2_not_using_in_text',
        mode: 'info',
        gp: 'h1-h6',
        show_title: 'کلمات استفاده نشده تگ H2 در متن',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          h2NotUsingInTextColumn,
          h2NotUsingInTextCountColumn
        ]
      },
      {
        title: 'h3_not_using_in_text_bool',
        link: 'h3_not_using_in_text',
        mode: 'info',
        gp: 'h1-h6',
        show_title: 'کلمات استفاده نشده تگ H3 در متن',
        count: 13,
        priority: 'low',
        columns: [
          urlColumn(), //✔️
          h3NotUsingInTextColumn,
          h3NotUsingInTextCountColumn
        ]
      }
    ]
  };
};

export const SimilarTitlesAndParagraphTitles = [similar_contentColumn];

export const SimilarTitlesAndParagraphUrls = [similar_title_urlColumn];
